import { useCallback, useEffect, useState } from 'react';
import { readCache, writeCache } from '../../../services/localCache';
import { createPortal } from 'react-dom';
import {
  Lock, Unlock, Banknote, Plus, X, Minus, Download, Printer,
  Settings, AlertTriangle, Copy, MessageCircle, Receipt, ShoppingBag, Search, ChevronDown, ChevronRight,
  CreditCard, Users, GraduationCap, User, NotebookPen, Sun, Moon, BadgeCheck, Ticket, DollarSign,
} from 'lucide-react';
import { turnoDeApertura } from '../../../utils/turnos';
import { Link } from 'react-router-dom';
import {
  getCashboxCurrent, openCashbox, closeCashbox, addCashboxMovement, addEntradaDolares, addSalidaDolares, getTipoCambio, pesosDeDolares, textoDolares,
  getCashboxConfig, putCashboxConfig, CashboxConfig, createPayment,
  getCashboxLastClose, getCashboxLedger, getCashboxMonth, cashboxExportUrl,
  LastClose, LedgerResp, CashboxMonth, CobroCajaCerrada, METHOD_LABEL,
  OpenCashbox, CashboxRow, money, TYPE_LABEL, PAY_METHOD_OPTIONS,
  createDebt, getStaffBuyers, getMemberDebts, StaffBuyer, sinPrefijoVitrina,
} from '../api';
import { listMembers, MemberRow } from '../../members/api';
import { getProducts, Product } from '../../../services/productsApi';
import DenominationModal, { borrarBorradorCaja } from './DenominationModal';
import ComisionPorEntregar from './ComisionPorEntregar';
import ClaseSueltaModal from './ClaseSueltaModal';
import Avatar from '../../../components/Avatar';
import PerfilLink, { rutaPerfil } from './PerfilLink';
import MetodoPago from './MetodoPago';
import { useRefrescoAlVolver } from '../../../hooks/useRefrescoAlVolver';
import EmptyState from '../../../components/EmptyState';
import CountUp from '../../../components/CountUp';
import { Skeleton } from '../../../components/Skeleton';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { setTurnosRuntime, getTurnoActual, horaBonita, TurnoActual } from '../../../utils/turnos';
import { buildCorteText, buildAperturaText, CorteData } from '../../../utils/corteReport';
import { avisarVitrina } from '../vitrinaApi';
import DolaresMostrador, { METODO_DOLARES, useDolares, registrarDolares } from './DolaresMostrador';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { colorDiferencia, nivelDiferencia, TOLERANCIA_MENOR, filasDesglose } from '../../../utils/caja';

function fmtDateTime(s: string | null) {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  return isNaN(d.getTime()) ? s : d.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
// Semáforo de caja (criterio de Karla): 🟢 cuadra · 🟡 diferencia ≤ $5 · 🔴 faltante/sobrante mayor.
// Los umbrales viven en utils/caja.ts, compartidos con el modal de conteo, la vista general y el
// texto del corte: antes cada uno tenía el suyo y el mismo arqueo se describía de tres maneras.
const diffColor = colorDiferencia;
function diffLabel(n: number) {
  if (nivelDiferencia(n) === 'cuadra') return 'cuadra';
  const dir = n < 0 ? `falta ${money(Math.abs(n))}` : `sobra ${money(n)}`;
  return nivelDiferencia(n) === 'menor' ? `${dir} (≤ $${TOLERANCIA_MENOR})` : dir;
}
/** ¿La caja abierta es de un día anterior? (corte pendiente) */
function esDeOtroDia(openedAt: string) {
  const d = new Date(openedAt.replace(' ', 'T'));
  return !isNaN(d.getTime()) && d.toDateString() !== new Date().toDateString();
}
/** «2026-08» → «Agosto 2026», para los encabezados de mes del historial. */
function mesBonito(ym: string) {
  const d = new Date(ym + '-01T12:00:00');
  if (isNaN(d.getTime())) return ym;
  const s = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** Modal para registrar un retiro, un depósito o «Entraron dólares» (cambio de divisa, 10-oct-2026). */
type MovKind = 'withdrawal' | 'deposit' | 'usd_in' | 'usd_out';
function MovementModal({ initialKind = 'withdrawal', onClose, onSaved }: { initialKind?: MovKind; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<MovKind>(initialKind);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [tc, setTc] = useState(17);
  useEffect(() => { getTipoCambio().then(setTc).catch(() => {}); }, []);
  const esUsd = kind === 'usd_in' || kind === 'usd_out';
  async function submit() {
    const a = Number(amount);
    if (!(a > 0)) { toast.error(esUsd ? 'Indica cuántos dólares' : 'Monto inválido'); return; }
    setSaving(true);
    try {
      if (kind === 'usd_in') await addEntradaDolares(a, tc, reason.trim() || undefined);
      else if (kind === 'usd_out') await addSalidaDolares(a, reason.trim() || undefined);
      else await addCashboxMovement(kind as 'withdrawal' | 'deposit', a, reason.trim() || undefined);
      onSaved();
    } catch (e: any) {
      if (e?.status === 409 && e?.data?.fx_rate) setTc(Number(e.data.fx_rate));
      toast.error(e?.message || 'No se pudo registrar');
    } finally { setSaving(false); }
  }
  const tab = (k: MovKind, txt: string) => (
    <button onClick={() => setKind(k)} data-testid={`mov-${k}`} className={`flex-1 whitespace-nowrap rounded-md px-1.5 py-1.5 text-xs font-medium sm:text-sm transition ${kind === k ? 'bg-accent text-on-accent' : 'text-content-2'}`}>{txt}</button>
  );
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-sm rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Movimiento de caja</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>
        <div className="mb-3 flex rounded-lg border border-line bg-surface p-0.5">
          {tab('withdrawal', 'Retiro')}{tab('deposit', 'Depósito')}{tab('usd_in', 'Entran USD')}{tab('usd_out', 'Salen USD')}
        </div>
        {kind === 'usd_out' && (
          <p className="mb-3 text-xs text-content-3" data-testid="mov-usd-salida-ayuda">
            Para sacar dólares del cajón (a la bóveda, a la dueña…). No mueve los pesos; al cerrar la caja ya no los espera.
          </p>
        )}
        {kind === 'usd_in' && (
          <p className="mb-3 text-xs text-content-3" data-testid="mov-usd-ayuda">
            Para cuando pagaron con dólares algo que se cobró en pesos (vitrina, parte en pesos y parte en dólares, o con cambio en pesos).
            Se cambian en la caja: salen los pesos y quedan los dólares, así el corte cuadra.
          </p>
        )}
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">{esUsd ? 'Dólares (USD)' : 'Monto (MXN)'}</label>
        <input inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} placeholder={esUsd ? '0' : '0.00'}
          data-testid="mov-monto"
          className="mb-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
        {kind === 'usd_in'
          ? <p className="mb-3 text-xs text-content-2" data-testid="mov-usd-pesos">× tipo de cambio <b className="text-content">{tc}</b> = salen <b className="text-content">{money(pesosDeDolares(Number(amount) || 0, tc))}</b> en pesos</p>
          : <div className="mb-2" />}
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">{esUsd ? 'Nota (opcional)' : 'Motivo'}</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={kind === 'usd_out' ? 'Ej. a la bóveda…' : esUsd ? 'Ej. agua de la vitrina, clase de Valeria…' : kind === 'withdrawal' ? 'Ej. compra de insumos, retiro a bóveda…' : 'Ej. fondo extra…'}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
        <div className="mt-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button disabled={saving} onClick={submit} data-testid="mov-registrar" className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
            {saving ? 'Guardando…' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>, document.body);
}

/** Modal post-apertura/corte: texto del reporte + enviar por WhatsApp (click-to-chat) / copiar. */
function WhatsAppReportModal({ title, text, phone, onClose }: { title: string; text: string; phone: string; onClose: () => void }) {
  const toast = useToast();
  const link = phone ? getWhatsAppLink(null, phone, text) : '';
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{title}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>
        <pre className="mb-4 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-xl border border-line bg-surface-2 p-3 text-xs leading-relaxed text-content-2">{text}</pre>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button onClick={() => { navigator.clipboard?.writeText(text).then(() => toast.success('Copiado')); }}
            className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content-2 transition hover:bg-surface-2">
            <Copy size={15} /> Copiar
          </button>
          {link && (
            <a href={link} target="_blank" rel="noreferrer" onClick={onClose}
              className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <MessageCircle size={15} /> Enviar por WhatsApp
            </a>
          )}
        </div>
      </div>
    </div>, document.body);
}

/** Venta de VITRINA (cobro rápido de mostrador): carrito de productos del catálogo core,
 *  un pago type='product' por renglón, estampado a la caja abierta (si la hay).
 *
 *  🧑‍🏫 19-sep-2026 — ¿QUIÉN COMPRA? Antes toda venta salía como «Público general»: no se podía
 *  asociar a una alumna ni a un maestro, y no había forma de FIAR. En Kydemy tampoco se le puede
 *  cobrar a un maestro, y recepción lo resolvía cargándolo a una alumna falsa («Pepita prueba»: 991
 *  ventas, $57 mil, con el comprador real escondido en el comentario). Ahora: Público (por omisión,
 *  un clic como siempre) · Alumna (buscador) · Maestro/equipo (lista). Con alumna o maestro elegido
 *  aparece «Fiar»: deja UN adeudo tipo `product` con el detalle, que se cobra después desde Deudas
 *  o desde la ficha. Al público no se le fía. */
type Comprador = { kind: 'publico' } | { kind: 'alumna'; m: MemberRow } | { kind: 'maestro'; u: StaffBuyer } | { kind: 'staff'; u: StaffBuyer };
type Modo = Comprador['kind'];
/** Quien compra por `payer_user_id` (equipo): maestro o staff. */
const esEquipo = (c: Comprador): c is { kind: 'maestro' | 'staff'; u: StaffBuyer } => c.kind === 'maestro' || c.kind === 'staff';

/** `alumnaInicial`: la abre «Registrar pago → Vitrina» de la ficha (Karla 28-sep-2026: ahí el cobro
 *  de vitrina era a mano, sin desglose de artículos). Arranca con esa alumna ya elegida.
 *  `paraFiar`: la abre «Registrar deuda → Vitrina» (Carlos 29-sep-2026: en Kydemy el fiado se
 *  registra como deuda desde el perfil, y aquí solo se llegaba entrando por «Registrar pago»).
 *  Mismo carrito, pero sin «Público», sin método de pago y sin «Cobrar»: solo deja el adeudo. */
export function VentaVitrinaModal({ onClose, onSold, alumnaInicial, paraFiar = false }: { onClose: () => void; onSold: () => void; alumnaInicial?: MemberRow | null; paraFiar?: boolean }) {
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string | null>(null); // filtro por categoría (null = todas)
  const [cart, setCart] = useState<Record<string, number>>({}); // product.id → qty
  const [method, setMethod] = useState('cash');
  const dol = useDolares();   // «Dólares» (Mabel 10-oct-2026): la venta va en pesos y los USD entran a la caja
  const [saving, setSaving] = useState(false);
  // ¿Quién compra?
  const [modo, setModo] = useState<Modo>(alumnaInicial || paraFiar ? 'alumna' : 'publico');
  const [comprador, setComprador] = useState<Comprador>(alumnaInicial ? { kind: 'alumna', m: alumnaInicial } : { kind: 'publico' });
  const [staff, setStaff] = useState<StaffBuyer[] | null>(null);
  // DESCUENTO DEL EQUIPO (Carlos 29-sep-2026): staff y maestros compran con −X % (ajuste `vitrina_equipo`,
  // Mi Estudio). Se aplica solo y se ve en el pie; se puede quitar para ESTA venta con la casilla.
  const [descuentoPct, setDescuentoPct] = useState(0);
  const [conDescuento, setConDescuento] = useState(true);
  const [qa, setQa] = useState('');
  const [alumnas, setAlumnas] = useState<MemberRow[]>([]);
  const [debeAlumna, setDebeAlumna] = useState<number | null>(null);

  useEffect(() => { getProducts().then((r) => setProducts(r.products)).finally(() => setLoading(false)); }, []);
  useEffect(() => {
    if ((modo === 'maestro' || modo === 'staff') && staff == null) {
      getStaffBuyers().then((r) => { setStaff(r.data); setDescuentoPct(Number(r.discount_pct) || 0); }).catch(() => setStaff([]));
    }
  }, [modo, staff]);
  useEffect(() => {
    if (modo !== 'alumna' || comprador.kind === 'alumna' || qa.trim().length < 2) { setAlumnas([]); return; }
    const t = setTimeout(() => { listMembers({ search: qa.trim(), page_size: 6 }).then((r) => setAlumnas(r.data)).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [qa, modo, comprador.kind]);
  // Al elegir alumna: ¿ya debe algo de vitrina? Recepción lo quiere ver ANTES de fiarle otra vez.
  useEffect(() => {
    setDebeAlumna(null);
    if (comprador.kind !== 'alumna') return;
    getMemberDebts(comprador.m.account_id).then((r) => setDebeAlumna(
      r.data.filter((d) => d.debt_type === 'product' || d.debt_type === 'other').reduce((t, d) => t + Number(d.remaining || 0), 0),
    )).catch(() => {});
  }, [comprador]);
  const cambiaModo = (m: Modo) => { setModo(m); setComprador({ kind: 'publico' }); setQa(''); };
  const hayComprador = comprador.kind !== 'publico';
  const quien = comprador.kind === 'alumna' ? { account_id: comprador.m.account_id }
    : esEquipo(comprador) ? { payer_user_id: comprador.u.user_id } : {};
  const nombreComprador = comprador.kind === 'alumna' ? `${comprador.m.firstname} ${comprador.m.lastname}`.trim()
    : esEquipo(comprador) ? comprador.u.name : '';
  // El descuento del equipo vive mientras compre staff o maestro y no se haya quitado a mano.
  const descuentoVivo = esEquipo(comprador) && descuentoPct > 0 && conDescuento ? descuentoPct : 0;
  const neto = (bruto: number) => Math.round(bruto * (100 - descuentoVivo)) / 100;

  const label = (p: Product) => (p.brand ? `${p.brand} — ${p.name}` : p.name);
  const cats = [...new Set(products.map((p) => p.category).filter(Boolean))] as string[];
  const filtered = products.filter((p) =>
    (!cat || p.category === cat) && label(p).toLowerCase().includes(q.trim().toLowerCase()));
  const lines = Object.entries(cart).filter(([, n]) => n > 0)
    .map(([id, qty]) => ({ p: products.find((x) => x.id === id)!, qty }))
    .filter((l) => l.p);
  const total = lines.reduce((s, l) => s + l.p.price * l.qty, 0);
  const inc = (id: string, d: number) => setCart((c) => ({ ...c, [id]: Math.max(0, (c[id] || 0) + d) }));

  async function cobrar() {
    if (!lines.length) { toast.error('Agrega al menos un producto'); return; }
    const enUsd = method === METODO_DOLARES;
    if (enUsd && !(dol.usdNum > 0)) { toast.error('Indica cuántos dólares te dio'); return; }
    setSaving(true);
    const hoy = new Date();
    const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    try {
      for (const l of lines) {
        await createPayment({
          ...quien,
          type: 'product', method: enUsd ? 'cash' : method, pay_date: fecha,
          // Con descuento del equipo va el NETO por renglón; el servidor reconstruye el descuento desde
          // el ajuste y lo guarda en amount_discount/amount_base (`team_discount`).
          amount: neto(l.p.price * l.qty),
          ...(descuentoVivo > 0 ? { team_discount: true } : {}),
          assoc_name: l.qty > 1 ? `${l.qty}× ${label(l.p)}` : label(l.p),
          product_id: Number(l.p.id),
          qty: l.qty,
        });
      }
      const avisoUsd = enUsd ? await registrarDolares(dol.usdNum, dol.tc, `Vitrina ${money(neto(total))}${nombreComprador ? ` · ${nombreComprador}` : ''}`, dol.setTc) : null;
      if (avisoUsd) toast.error(avisoUsd);
      const cambio = enUsd ? Math.round((pesosDeDolares(dol.usdNum, dol.tc) - neto(total)) * 100) / 100 : 0;
      toast.success(`Venta registrada · ${money(neto(total))}${enUsd ? ` · ${dol.usdNum} USD${cambio > 0 ? ` · cambio ${money(cambio)}` : ''}` : ''}${descuentoVivo ? ` (−${descuentoVivo}% equipo)` : ''}${nombreComprador ? ` · ${nombreComprador}` : ''}`);
      avisarVitrina();
      onSold();
    } catch (e: any) { toast.error(e?.message || 'No se pudo registrar la venta'); }
    finally { setSaving(false); }
  }

  /** FIAR: no entra dinero ni se toca la caja. Queda UN adeudo con el detalle del carrito. */
  async function fiar() {
    if (!lines.length) { toast.error('Agrega al menos un producto'); return; }
    if (!hayComprador) { toast.error('Para fiar, elige a la alumna o al maestro'); return; }
    setSaving(true);
    try {
      const detalle = lines.map((l) => `${l.qty}× ${label(l.p)}`).join(' · ');
      await createDebt({
        // Con descuento del equipo el adeudo nace por el NETO y lo dice en el comentario (el adeudo no
        // tiene columnas de descuento); la ficha lo enseña como «… · −20% equipo».
        ...quien, amount: neto(total), debt_type: 'product',
        comment: `${descuentoVivo ? `Vitrina (−${descuentoVivo}% equipo)` : 'Vitrina'}: ${detalle}`.slice(0, 255),
        // Los artículos además van en renglones: la mercancía sale de existencias al fiar.
        lines: lines.map((l) => ({ product_id: Number(l.p.id), qty: l.qty })),
      });
      avisarVitrina();
      toast.success(`Fiado a ${nombreComprador} · ${money(neto(total))}${descuentoVivo ? ` (−${descuentoVivo}% equipo)` : ''}`);
      onSold();
    } catch (e: any) { toast.error(e?.message || 'No se pudo registrar el fiado'); }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative flex max-h-[92vh] w-full max-w-md flex-col rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><ShoppingBag size={18} /> {paraFiar ? 'Fiar de vitrina' : 'Venta de vitrina'}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : products.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line px-3 py-4 text-sm text-content-3">
            La vitrina está vacía. Da de alta tus productos en la sección <b className="text-content-2">Vitrina</b> del menú.
          </p>
        ) : (
          <>
            {/* ¿QUIÉN COMPRA? Público por omisión: la venta normal sigue siendo de un clic. */}
            <div className="mb-2" data-testid="vitrina-comprador">
              <div className="flex rounded-lg border border-line bg-surface p-0.5">
                {/* Staff (Karla, Dayan, Mabel, Aracely) aparte de Maestro (Carlos 29-sep); los dos con descuento. */}
                {([['publico', 'Público', Users], ['alumna', 'Alumna', GraduationCap], ['maestro', 'Maestro', User], ['staff', 'Staff', BadgeCheck]] as const).filter(([k]) => !paraFiar || k !== 'publico').map(([k, txt, Icon]) => (
                  <button key={k} onClick={() => cambiaModo(k)} data-testid={`comprador-${k}`}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition ${modo === k ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                    <Icon size={14} /> {txt}
                  </button>
                ))}
              </div>
              {modo === 'alumna' && (comprador.kind === 'alumna' ? (
                <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-accent/50 bg-accent/10 px-2.5 py-1.5 text-sm">
                  <GraduationCap size={14} className="shrink-0 text-content-2" />
                  <span className="min-w-0 flex-1 truncate text-content">{nombreComprador}</span>
                  {debeAlumna != null && debeAlumna > 0 && <span className="shrink-0 text-xs font-medium text-amber-600 dark:text-amber-400">ya debe {money(debeAlumna)}</span>}
                  <button onClick={() => { setComprador({ kind: 'publico' }); setQa(''); }} className="shrink-0 text-content-3 hover:text-content"><X size={14} /></button>
                </div>
              ) : (
                <div className="relative mt-1.5">
                  <input autoFocus value={qa} onChange={(e) => setQa(e.target.value)} placeholder="Buscar alumna por nombre…" data-testid="comprador-buscar"
                    className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
                  {alumnas.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                      {alumnas.map((m) => (
                        <button key={m.account_id} onClick={() => setComprador({ kind: 'alumna', m })}
                          className="block w-full truncate px-3 py-1.5 text-left text-sm text-content hover:bg-surface-2">{m.firstname} {m.lastname}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(modo === 'maestro' || modo === 'staff') && (
                <div className="mt-1.5 flex max-h-24 flex-wrap gap-1 overflow-y-auto" data-testid={`lista-${modo}`}>
                  {staff == null ? <span className="text-xs text-content-3">Cargando equipo…</span>
                    : staff.filter((u) => u.kind === modo).length === 0 ? <span className="text-xs text-content-3">Nadie marcado como {modo} (se elige en Mi Estudio).</span>
                    : staff.filter((u) => u.kind === modo).map((u) => {
                      const sel = esEquipo(comprador) && comprador.u.user_id === u.user_id;
                      return (
                        <button key={u.user_id} onClick={() => setComprador(sel ? { kind: 'publico' } : { kind: modo, u })}
                          className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${sel ? 'bg-accent text-on-accent' : 'border border-line text-content-2 hover:bg-surface-2'}`}>
                          {u.name.split(' ').slice(0, 2).join(' ')}
                          {u.debe > 0 && <span className={`ml-1 ${sel ? '' : 'text-amber-600 dark:text-amber-400'}`}>· debe {money(u.debe)}</span>}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Chips de categoría (Bebidas / Snacks / Merch…) */}
            {cats.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                <button onClick={() => setCat(null)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${!cat ? 'bg-accent text-on-accent' : 'border border-line text-content-2 hover:bg-surface-2'}`}>
                  Todo
                </button>
                {cats.map((c) => (
                  <button key={c} onClick={() => setCat(cat === c ? null : c)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${cat === c ? 'bg-accent text-on-accent' : 'border border-line text-content-2 hover:bg-surface-2'}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
            <div className="relative mb-2">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto…"
                className="w-full rounded-lg border border-line bg-surface py-2 pl-8 pr-3 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
              {filtered.map((p) => {
                const qty = cart[p.id] || 0;
                return (
                  <div key={p.id} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm transition ${qty > 0 ? 'border-accent/50 bg-accent/10' : 'border-line bg-surface'}`}>
                    <button onClick={() => inc(p.id, 1)} className="flex min-w-0 flex-1 items-center gap-2 text-left text-content">
                      {p.photoUrl ? (
                        <img src={p.photoUrl} alt="" className="h-9 w-9 shrink-0 rounded-md border border-line object-cover" />
                      ) : (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface-2 text-content-3"><ShoppingBag size={15} /></span>
                      )}
                      <span className="min-w-0">
                        <span className="block truncate">{label(p)}</span>
                        {p.description && <span className="block truncate text-xs text-content-3">{p.description}</span>}
                      </span>
                    </button>
                    <span className="shrink-0 text-xs tabular-nums text-content-3">{money(p.price)}</span>
                    {qty > 0 ? (
                      <span className="flex shrink-0 items-center gap-1">
                        <button onClick={() => inc(p.id, -1)} className="rounded-md border border-line p-1 text-content-2 hover:bg-surface-2"><Minus size={12} /></button>
                        <span className="w-6 text-center text-sm font-semibold tabular-nums text-content">{qty}</span>
                        <button onClick={() => inc(p.id, 1)} className="rounded-md border border-line p-1 text-content-2 hover:bg-surface-2"><Plus size={12} /></button>
                      </span>
                    ) : (
                      <button onClick={() => inc(p.id, 1)} className="shrink-0 rounded-md border border-line p-1 text-content-3 hover:bg-surface-2 hover:text-content"><Plus size={14} /></button>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && <p className="px-2 py-3 text-sm text-content-3">Nada coincide con "{q}".</p>}
            </div>

            {/* Pie PEGADO al fondo del modal: antes iba detrás del catálogo entero y en la tablet
                había que bajar por los 27 productos para ver con qué se cobraba (Karla, 18-sep).
                Los métodos van con su icono, igual que en el resto de la app. */}
            <div className="sticky bottom-0 -mx-5 mt-3 border-t border-line bg-surface px-5 pb-1 pt-3">
              {paraFiar ? (
                // Desde «Registrar deuda»: no entra dinero, así que ni método ni «Cobrar».
                <p className="mb-2 text-xs text-content-3" data-testid="vitrina-fiar-aviso">
                  No entra dinero a la caja: queda como adeudo{nombreComprador ? <> de <b className="text-content-2">{nombreComprador}</b></> : ''} y se cobra después desde su ficha.
                </p>
              ) : (
              <>
              {method === METODO_DOLARES && <DolaresMostrador total={neto(total)} usd={dol.usd} setUsd={dol.setUsd} tc={dol.tc} />}
              <div className="mb-2 flex rounded-lg border border-line bg-surface p-0.5">
                {[...PAY_METHOD_OPTIONS, { v: METODO_DOLARES, label: 'Dólares' }].map((o) => (
                  <button key={o.v} onClick={() => setMethod(o.v)}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition [&_svg]:text-current ${method === o.v ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                    <MetodoPago metodo={o.v} size={14} />
                    {o.label}
                  </button>
                ))}
              </div>
              </>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 text-sm text-content-2">
                  {lines.reduce((s, l) => s + l.qty, 0)} artículo(s) ·{' '}
                  {descuentoVivo > 0 && <s className="text-content-3">{money(total)}</s>}{descuentoVivo > 0 && ' '}
                  <b className="font-display text-lg text-content">{money(neto(total))}</b>
                  {/* Se DICE al comprar o fiar (Carlos): descuento del equipo visible, con casilla para quitarlo. */}
                  {esEquipo(comprador) && descuentoPct > 0 && (
                    <label className="ml-2 inline-flex cursor-pointer items-center gap-1 align-middle text-xs text-emerald-700 dark:text-emerald-400" data-testid="descuento-equipo">
                      <input type="checkbox" checked={conDescuento} onChange={(e) => setConDescuento(e.target.checked)} className="h-3.5 w-3.5 accent-emerald-600" />
                      −{descuentoPct}% {comprador.kind === 'staff' ? 'staff' : 'maestro'}{conDescuento ? ` · −${money(total - neto(total))}` : ' (sin aplicar)'}
                    </label>
                  )}
                </span>
                {paraFiar ? (
                  <button disabled={saving || !lines.length || !hayComprador} onClick={fiar} data-testid="vitrina-fiar"
                    title={hayComprador ? `Dejarlo como adeudo de ${nombreComprador}` : 'Elige a la alumna o al maestro'}
                    className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                    <NotebookPen size={14} /> {saving ? 'Guardando…' : 'Registrar deuda'}
                  </button>
                ) : (
                  <>
                {/* Fiar: solo con alumna o maestro elegido. No mueve la caja; deja un adeudo. */}
                {hayComprador && (
                  <button disabled={saving || !lines.length} onClick={fiar} data-testid="vitrina-fiar" title={`Dejarlo como adeudo de ${nombreComprador}`}
                    className="ml-auto flex items-center gap-1 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-300">
                    <NotebookPen size={14} /> Fiar
                  </button>
                )}
                <button disabled={saving || !lines.length} onClick={cobrar} data-testid="vitrina-cobrar"
                  className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                  {saving ? 'Cobrando…' : 'Cobrar'}
                </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>, document.body);
}

const DIAS = ['D', 'L', 'M', 'Mi', 'J', 'V', 'S'];

/** Modal de configuración de la caja (horario→turnos + corte por WhatsApp). Solo admin. */
function ConfigModal({ cfg, onClose, onSaved }: { cfg: CashboxConfig; onClose: () => void; onSaved: (c: CashboxConfig) => void }) {
  const toast = useToast();
  const [openingTime, setOpeningTime] = useState(cfg.openingTime);
  const [closingTime, setClosingTime] = useState(cfg.closingTime);
  const [days, setDays] = useState<number[]>(cfg.workingDays);
  const [splitOn, setSplitOn] = useState(!!cfg.cashShiftSplit);
  const [split, setSplit] = useState(cfg.cashShiftSplit || '15:00');
  const [corteOn, setCorteOn] = useState(cfg.corte.enabled);
  const [phone, setPhone] = useState(cfg.corte.phone || '');
  const [aperturaOn, setAperturaOn] = useState(cfg.corte.apertura);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      const saved = await putCashboxConfig({
        openingTime, closingTime, workingDays: days,
        cashShiftSplit: splitOn ? split : null,
        corte: { enabled: corteOn, phone: phone.replace(/\D/g, ''), apertura: corteOn && aperturaOn },
      });
      onSaved(saved);
      toast.success('Configuración de caja guardada');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }

  const inputCls = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Configurar caja</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Abre</label>
            <input type="time" value={openingTime} onChange={(e) => setOpeningTime(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Cierra</label>
            <input type="time" value={closingTime} onChange={(e) => setClosingTime(e.target.value)} className={inputCls} />
          </div>
        </div>

        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Días de operación</label>
        <div className="mb-3 flex gap-1.5">
          {DIAS.map((d, i) => (
            <button key={i} onClick={() => setDays((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i].sort())}
              className={`h-9 w-9 rounded-lg border text-sm font-medium transition ${days.includes(i) ? 'border-transparent bg-accent-solid text-on-accent' : 'border-line bg-surface text-content-3 hover:text-content'}`}>
              {d}
            </button>
          ))}
        </div>

        <label className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
          <span className="text-sm text-content-2">Dividir el día en 2 turnos (matutino / vespertino)</span>
          <input type="checkbox" checked={splitOn} onChange={(e) => setSplitOn(e.target.checked)} className="h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
        </label>
        {splitOn && (
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">El turno vespertino empieza a las</label>
            <input type="time" value={split} onChange={(e) => setSplit(e.target.value)} className={inputCls} />
          </div>
        )}

        <label className="mb-1 flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
          <span className="text-sm text-content-2">Al hacer el corte, preparar reporte por WhatsApp</span>
          <input type="checkbox" checked={corteOn} onChange={(e) => setCorteOn(e.target.checked)} className="h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
        </label>
        {corteOn && (
          <div className="mb-1 mt-2 space-y-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">WhatsApp que recibe el corte (10 dígitos)</label>
              <input inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ''))} placeholder="6861234567" className={inputCls} />
            </div>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
              <span className="text-sm text-content-2">Avisar también la apertura de caja</span>
              <input type="checkbox" checked={aperturaOn} onChange={(e) => setAperturaOn(e.target.checked)} className="h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
            </label>
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button disabled={saving} onClick={submit} className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>, document.body);
}

/** Chip de color por tipo de renglón — el código de colores de Kydemy. */
const CHIP: Record<string, { txt: string; cls: string }> = {
  open: { txt: 'Apertura', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  deposit: { txt: 'Depósito', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  withdrawal: { txt: 'Retiro', cls: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  payment: { txt: 'Cobro', cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  close: { txt: 'Cierre', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  // Fiado del turno: alguien se llevó algo y quedó debiendo. Naranja = el color de Vitrina.
  debt: { txt: 'Debe', cls: 'bg-orange-500/20 text-orange-700 dark:text-orange-300' },
};

/** Lee el JSON del conteo y lo resume: «3×$500 · 2×$200 · 1×$50». */
function resumeBreakdown(json?: string | null): string | null {
  if (!json) return null;
  try {
    const d = JSON.parse(json);
    const mxn = d?.mxn || {};
    // Lista única de denominaciones (utils/caja.ts): el $20 moneda sale como «$20 moneda».
    const partes = filasDesglose(mxn).map((f) => `${f.n}×${f.etiqueta}`);
    return partes.length ? partes.join(' · ') : null;
  } catch { return null; }
}

/** Tarjeta-botón del conteo de billetes de un turno (apertura o cierre). Con conteo: clic → detalle
 *  (BilletesModal). Sin conteo: sin clic, y dice por qué.
 *  29-sep-2026 (Carlos: «normaliza el gris»): la tarjeta sin conteo iba con borde punteado, sin fondo
 *  y con el importe en gris apagado, así que los días de antes se veían distintos a los de ahora.
 *  Las dos comparten caja (borde, fondo, importe); solo cambian el icono, la leyenda y «Ver detalle». */
function TarjetaBilletes({ etapa, json, importe, kydemy, onVer }: {
  etapa: 'Apertura' | 'Cierre'; json?: string | null; importe?: number | string | null; kydemy?: boolean; onVer: () => void;
}) {
  const hay = !!resumeBreakdown(json);
  const monto = importe != null ? money(Number(importe)) : '—';
  const cuerpo = (
    <>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${hay ? 'bg-accent/15 text-accent-ink' : 'bg-surface-2 text-content-3'}`}>
        <Banknote size={18} />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-content-3">Billetes · {etapa}</span>
        <span className="block font-display text-sm font-bold tabular-nums text-content">{monto}</span>
        {!hay && (
          <span className="block truncate text-[11px] text-content-3">
            {kydemy ? 'Sin conteo · Kydemy no guarda billetes' : 'Sin conteo de billetes'}
          </span>
        )}
      </span>
      {hay && (
        <span className="flex shrink-0 items-center gap-0.5 text-xs font-semibold text-accent-content">
          Ver detalle <ChevronRight size={14} className="transition group-hover:translate-x-0.5" />
        </span>
      )}
    </>
  );
  return hay ? (
    <button onClick={onVer} data-testid={`ver-billetes-${etapa.toLowerCase()}`}
      className="group flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5 shadow-sm transition hover:border-accent-solid/60 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40">
      {cuerpo}
    </button>
  ) : (
    <div data-testid={etapa === 'Cierre' ? 'cierre-sin-conteo' : 'apertura-sin-conteo'}
      title={kydemy ? 'Este turno se registró en Kydemy, que solo guarda el importe, no los billetes.' : 'En este turno no se contaron billetes; solo hay importe.'}
      className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5 shadow-sm">
      {cuerpo}
    </div>
  );
}

/** Hasta qué denominación cuenta como «cambio»: con billetes de $50 o menos y monedas se da cambio;
 *  un cajón con $1,500 en tres billetes de $500 está lleno y aun así no puede cobrar nada. */
const CAMBIO_HASTA = 50;

/**
 * «¿Con qué billetes se abrió la caja?» — de UN clic desde la caja abierta (Carlos, 19-sep-2026).
 * Karla lo mira al llegar para saber si hay que ir por cambio. Antes el conteo solo salía como una
 * línea de texto chico al final de los movimientos del día, debajo de toda la tabla.
 */
function BilletesModal({ titulo, json, importe, nota, onClose }: {
  titulo: string; json?: string | null; importe: number; nota?: string; onClose: () => void;
}) {
  let mxn: Record<string, number> = {}; let heredado = false;
  try { const d = json ? JSON.parse(json) : null; mxn = d?.mxn || {}; heredado = d?.heredado === true; } catch { /* sin desglose */ }
  // Lista única (utils/caja.ts): desde el 1-oct-2026 el $20 viene en billete ('20') y en moneda ('20m').
  const filas = filasDesglose(mxn).map((f) => ({ ...f, d: f.valor }));
  const billetes = filas.filter((f) => !f.moneda);
  const monedas = filas.filter((f) => f.moneda);
  const suma = (fs: typeof filas) => fs.reduce((t, f) => t + f.total, 0);
  const cambio = suma(filas.filter((f) => f.d <= CAMBIO_HASTA));
  const grupo = (nombre: string, fs: typeof filas) => fs.length > 0 && (
    <div className="mt-3">
      <div className="mb-1 flex justify-between text-[11px] font-medium uppercase tracking-wide text-content-3">
        <span>{nombre}</span><span className="tabular-nums">{money(suma(fs))}</span>
      </div>
      {fs.map((f) => (
        <div key={f.k} className="flex items-center justify-between border-t border-line/50 py-1 text-sm">
          <span className="w-28 font-medium text-content">{f.etiqueta}</span>
          <span className="tabular-nums text-content-2">× {f.n}</span>
          <span className="w-24 text-right tabular-nums text-content">{money(f.total)}</span>
        </div>
      ))}
    </div>
  );
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" data-testid="billetes-modal">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-sm overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><Banknote size={18} /> {titulo}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>
        <p className="text-sm text-content-2">Importe: <b className="tabular-nums text-content">{money(importe)}</b>{nota ? ` · ${nota}` : ''}</p>
        {filas.length === 0 ? (
          <p className="mt-4 rounded-lg bg-surface-2 px-3 py-3 text-sm text-content-2" data-testid="billetes-sin-conteo">
            Esta caja se registró <b>solo con el importe</b>, sin contar billetes. Para saber con qué
            billetes y monedas arranca el día, al abrir o cerrar usa <b>«Contar billetes»</b>.
          </p>
        ) : (
          <>
            {heredado && (
              <p className="mt-2 rounded-lg bg-surface-2 px-3 py-2 text-xs text-content-3">
                Nadie contó al abrir: son los billetes con que <b>cerró el turno anterior</b> (se abrió con ese mismo importe).
              </p>
            )}
            <div className={`mt-3 rounded-xl border px-3 py-2.5 text-sm ${cambio < 200
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`} data-testid="billetes-cambio">
              Para dar cambio (billetes de ${CAMBIO_HASTA} o menos y monedas): <b className="tabular-nums">{money(cambio)}</b>
              {cambio < 200 && <span className="block text-xs">Poco cambio: conviene conseguir billetes chicos o monedas.</span>}
            </div>
            {grupo('Billetes', billetes)}
            {grupo('Monedas', monedas)}
            <div className="mt-3 flex justify-between border-t border-line pt-2 text-sm font-semibold">
              <span className="text-content-2">Total</span><span className="tabular-nums text-content">{money(suma(filas))}</span>
            </div>
          </>
        )}
      </div>
    </div>, document.body);
}

/** Un día de la columna izquierda: chip Actual/Cerrada + DIFERENCIA del arqueo.
 *
 * 🔎 18-sep-2026 (lo cazó Carlos comparando con Kydemy): la cifra grande de la lista de días
 * de Kydemy es la **diferencia**, no el cierre. Por eso allá casi todas salen «$ 0.00» en
 * verde —quiere decir «cuadró»— y solo destaca el día que descuadró ($15.00 el 2-sep).
 * Nosotros pintábamos ahí el `close_amount`, así que la misma columna se leía como una
 * ristra de cifras distintas y no se veía de un vistazo qué día falló. El cierre NO se
 * pierde: baja a la línea pequeña, junto al turno.
 */
function DiaItem({ c, activo, onClick }: { c: CashboxRow; activo: boolean; onClick: () => void }) {
  // 🪤 Una caja que sigue ABIERTA en Kydemy se importa con status='closed' (el ETL lo fija
  // duro) pero sin `closed_at` ni importes. Sin esto salía como «Cerrada $0.00», que es
  // justo lo que no es. Mientras no tenga cierre, se muestra en curso.
  const abierta = c.status === 'open' || !c.closed_at;
  const dif = c.difference == null ? null : Number(c.difference);
  const cuando = c.closed_at || c.opened_at;
  const d = new Date(String(cuando).replace(' ', 'T'));
  const dia = isNaN(d.getTime()) ? String(cuando).slice(0, 10)
    : d.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' });
  const cierre = abierta ? c.open_amount : c.close_amount;
  // Sol = matutino, luna = vespertino (mismos colores que los turnos de la agenda dental).
  const turno = turnoDeApertura(c.opened_at, c.shift_label);
  const signo = dif == null || dif === 0 ? '' : dif > 0 ? '+' : '−';
  return (
    <button onClick={onClick}
      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left transition ${
        activo ? 'bg-accent text-on-accent' : 'hover:bg-surface-2'}`}>
      <span className="min-w-0">
        <span className={`flex items-center gap-1.5 text-sm ${activo ? '' : 'text-content'}`}>
          <span className="truncate">{dia}</span>
          {turno && (
            <span title={turno === 'matutino' ? 'Turno matutino' : 'Turno vespertino'} aria-label={turno === 'matutino' ? 'Turno matutino' : 'Turno vespertino'}
              className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                activo ? 'bg-black/15'
                  : turno === 'matutino' ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300'
                                         : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300'}`}>
              {turno === 'matutino' ? <Sun size={12} /> : <Moon size={12} />}
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
            activo ? 'bg-black/15'
                   : abierta ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300'
                             : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
            {abierta ? 'En curso' : 'Cerrada'}
          </span>
          {c.shift_label && <span className={`text-[10px] ${activo ? 'opacity-70' : 'text-content-3'}`}>{c.shift_label}</span>}
          {cierre != null && (
            <span className={`text-[10px] tabular-nums ${activo ? 'opacity-70' : 'text-content-3'}`}>
              {abierta ? 'fondo' : 'cierre'} {money(cierre)}
            </span>
          )}
        </span>
      </span>
      <span title={dif == null ? 'Sin cierre todavía' : diffLabel(dif)}
        className={`shrink-0 text-sm font-semibold tabular-nums ${
          activo ? '' : dif == null ? 'text-content-3' : diffColor(dif)}`}>
        {dif == null ? '—' : `${signo}${money(Math.abs(dif))}`}
      </span>
    </button>
  );
}

export default function CashboxPanel({ recarga = 0 }: { recarga?: number } = {}) {
  const { user, hasModule } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  // Cajera sin Finanzas (Carlos 29-sep-2026): solo los cortes de HOY — sin meses, sin Excel del
  // mes y sin configurar la caja. El servidor ya le devuelve solo lo de hoy.
  const soloHoy = !hasModule('letsmove_finances');
  const [venta, setVenta] = useState(false);
  const [claseSuelta, setClaseSuelta] = useState(false);
  const [current, setCurrent] = useState<OpenCashbox | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'open' | 'close' | 'movement' | 'config' | null>(null);
  const [movKind, setMovKind] = useState<MovKind>('withdrawal');
  const [config, setConfig] = useState<CashboxConfig | null>(null);
  const [turno, setTurno] = useState<TurnoActual | null>(null);
  const [waModal, setWaModal] = useState<{ title: string; text: string } | null>(null);
  const [verBilletes, setVerBilletes] = useState<{ titulo: string; json?: string | null; importe: number; nota?: string } | null>(null);
  const [lastClose, setLastClose] = useState<LastClose | null>(null);
  // Cobros registrados con la caja CERRADA desde el último cierre: los adopta la caja que se abra (1-oct-2026).
  const [pendientes, setPendientes] = useState<CobroCajaCerrada[]>([]);
  const [pendienteEfectivo, setPendienteEfectivo] = useState(0);
  const [pendienteUsd, setPendienteUsd] = useState(0);   // dólares cobrados con la caja cerrada (1-oct-2026)

  // ── Columna de días (rediseño 18-sep-2026) ──
  // Karla mira la caja por DÍA: la lista está siempre a la vista, con su mes, y al elegir un
  // día se cargan sus movimientos a la derecha. Sustituye al «Historial de arqueos» colapsado.
  const [mes, setMes] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [meses, setMeses] = useState<CashboxMonth[]>([]);
  const [dias, setDias] = useState<CashboxRow[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  // Solo aplica por debajo de `lg`: en escritorio la lista está siempre desplegada.
  const [listaAbierta, setListaAbierta] = useState(false);
  const [ledger, setLedger] = useState<LedgerResp | null>(null);
  const [cargandoLedger, setCargandoLedger] = useState(false);

  const reload = useCallback(() => {
    const cached = readCache<{ current: OpenCashbox | null }>('fin-cashbox');
    if (cached) { setCurrent(cached.current); setLoading(false); } else setLoading(true);
    Promise.all([getCashboxCurrent(), getCashboxLastClose()])
      .then(([c, l]) => {
        setCurrent(c.open); setLastClose(l.last);
        setPendientes(l.pendientes || []); setPendienteEfectivo(Number(l.pendiente_efectivo) || 0); setPendienteUsd(Number(l.pendiente_usd) || 0);
        writeCache('fin-cashbox', { current: c.open });
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Días del mes elegido. Al cambiar de mes se selecciona el más reciente, que es el que
  // interesa al entrar (la caja de hoy).
  const cargaMes = useCallback((ym: string, autoSel: boolean) => {
    getCashboxMonth(ym).then((r) => {
      setDias(r.data);
      if (r.months?.length) setMeses(r.months);
      if (autoSel) setSel(r.data.length ? r.data[0].cashbox_id : null);
    }).catch(() => { setDias([]); if (autoSel) setSel(null); });
  }, []);
  useEffect(() => { cargaMes(mes, true); }, [mes, cargaMes]);

  // Movimientos del día seleccionado.
  useEffect(() => {
    if (sel == null) { setLedger(null); return; }
    setCargandoLedger(true);
    getCashboxLedger(sel).then(setLedger).catch(() => setLedger(null)).finally(() => setCargandoLedger(false));
  }, [sel]);

  const refrescaTodo = useCallback(() => {
    reload();
    cargaMes(mes, false);
    if (sel != null) getCashboxLedger(sel).then(setLedger).catch(() => {});
  }, [reload, cargaMes, mes, sel]);

  useEffect(() => {
    getCashboxConfig().then((cfg) => {
      setConfig(cfg);
      setTurnosRuntime({ openingTime: cfg.openingTime, closingTime: cfg.closingTime, workingDays: cfg.workingDays, split: cfg.cashShiftSplit });
      setTurno(getTurnoActual());
    }).catch(() => setTurno(getTurnoActual()));
  }, []);

  function applyConfig(cfg: CashboxConfig) {
    setConfig(cfg);
    setTurnosRuntime({ openingTime: cfg.openingTime, closingTime: cfg.closingTime, workingDays: cfg.workingDays, split: cfg.cashShiftSplit });
    setTurno(getTurnoActual());
    setModal(null);
  }

  const hoyISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Borrador del conteo (5-oct-2026, Mabel): uno por caja y acción; se borra al guardar con éxito.
  const borradorAbrir = `lm-caja-borrador:abrir:${lastClose?.cashbox_id ?? 0}`;
  const borradorCerrar = current ? `lm-caja-borrador:cerrar:${current.cashbox_id}` : undefined;
  async function doOpen(amount: number, breakdown: string) {
    try {
      const t = getTurnoActual();
      const r: any = await openCashbox(amount, breakdown, t ? { shift_type: t.tipo, shift_label: t.label, fecha: hoyISO() } : { fecha: hoyISO() });
      const d = r?.open_difference;
      const nAdop = (r?.adoptados || []).length;
      if (nAdop) toast.info(`Se sumaron a esta caja ${nAdop} cobro${nAdop === 1 ? '' : 's'} registrado${nAdop === 1 ? '' : 's'} con la caja cerrada${r.adoptado_efectivo ? ` (${money(r.adoptado_efectivo)} en efectivo)` : ''}`);
      if (d == null) toast.success('Caja abierta');
      else if (Number(d) === 0) toast.success('Caja abierta · cuadra con lo que dejó el turno anterior');
      else toast.info(`Caja abierta · ${diffLabel(Number(d))} contra lo que dejó el turno anterior`);
      setModal(null); borrarBorradorCaja(borradorAbrir);
      // La caja recién abierta pasa a ser el día seleccionado.
      const nuevo = r?.cashbox_id ?? null;
      reload(); cargaMes(mes, false);
      if (nuevo) setSel(nuevo);
      // Turno nuevo → toca contar la vitrina (si el inventario está encendido, el conteo se abre solo).
      avisarVitrina({ abrirConteo: true });
      if (config?.corte.enabled && config.corte.apertura && config.corte.phone) {
        setWaModal({ title: 'Aviso de apertura', text: buildAperturaText({ label: t?.label || 'Único', fondo: amount, usuario: user ? `${user.firstname} ${user.lastname}`.trim() : undefined }) });
      }
    } catch (e: any) { toast.error(e?.message || 'No se pudo abrir la caja'); }
  }
  async function doClose(amount: number, breakdown: string, motivo?: string) {
    try {
      const r = await closeCashbox(amount, motivo, breakdown);
      const d = r.difference;
      toast[d === 0 ? 'success' : 'info'](d === 0 ? 'Caja cerrada · cuadra exacto' : `Caja cerrada · ${diffLabel(d)}`);
      setModal(null); borrarBorradorCaja(borradorCerrar); refrescaTodo();
      avisarVitrina();
      if (config?.corte.enabled && config.corte.phone) {
        setWaModal({ title: 'Corte de caja', text: buildCorteText(r as CorteData) });
      }
    } catch (e: any) { toast.error(e?.message || 'No se pudo cerrar la caja'); }
  }

  // La tablet del mostrador se queda abierta aquí: que se entere de los cobros nuevos sola.
  useRefrescoAlVolver(refrescaTodo, 120000);
  // El botón «Actualizar» de la cabecera acaba de traer datos de Kydemy: repescarlos.
  useEffect(() => { if (recarga > 0) refrescaTodo(); }, [recarga]);   // eslint-disable-line react-hooks/exhaustive-deps

  const cortePendiente = current ? esDeOtroDia(current.opened_at) : false;
  const box = ledger?.box || null;
  const verArqueoAbierto = box?.status === 'open';
  // Fiados de la caja ABIERTA, para el resumen de arriba. Solo si los movimientos a la vista son
  // los de esa caja (al mirar un día pasado, el resumen no debe enseñar los fiados de otro turno).
  const fiadoTurno = current && box && box.cashbox_id === current.cashbox_id && ledger?.totales
    ? { pendiente: ledger.totales.fiado_pendiente || 0, n: ledger.totales.fiados_n || 0,
        alumnas: ledger.totales.fiado_alumnas || 0, maestros: ledger.totales.fiado_maestros || 0 } : null;

  // 💵 18-sep-2026 — «Efectivo» ES EL CAJÓN: aquí solo entra lo que lo mueve.
  // Medido contra Kydemy: en sus 18 cajas cerradas de septiembre, su `num_payments`/
  // `total_payments` coinciden EXACTAMENTE con los cobros en efectivo, nunca con el total.
  // Su pestaña Cashbox ni siquiera tiene columna de método porque allí todo es efectivo por
  // definición; las tarjetas y transferencias viven en Payments (nuestra pestaña Ingresos).
  // Antes los pintábamos aquí con «—» en Acumulado: los totales salían bien, pero la columna
  // CANTIDAD no sumaba lo que hay en el cajón (el 17-sep se leían $227 y había $99).
  // No se esconden: van resumidos en el pie, con un clic a Ingresos.
  // 29-sep-2026 — y los FIADOS del turno (`debt`): no mueven el cajón, pero si alguien se llevó una
  // botella sin pagar, la Caja tiene que decirlo (Carlos). Van resaltados y sin acumulado.
  const filasEfectivo = (ledger?.rows || []).filter((f) => f.afecta_efectivo || f.kind === 'close' || f.kind === 'debt');
  const otrosMetodos = (() => {
    const fuera = (ledger?.rows || []).filter((f) => f.kind === 'payment' && !f.afecta_efectivo);
    const porMetodo = new Map<string, number>();
    for (const f of fuera) porMetodo.set(f.method, (porMetodo.get(f.method) || 0) + 1);
    const detalle = [...porMetodo.keys()]
      .map((m) => (PAY_METHOD_OPTIONS.find((o) => o.v === m)?.label || m).toLowerCase())
      .join(' y ');
    return { n: fuera.length, total: fuera.reduce((t, f) => t + f.amount, 0), detalle };
  })();

  return (
    <div>
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : (
        <>
          {cortePendiente && current && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle size={16} className="shrink-0" />
              <span>La caja {current.shift_label ? `del turno ${current.shift_label} ` : ''}abierta el {fmtDateTime(current.opened_at)} quedó <b>sin corte</b>. Hay que cerrarla antes de abrir la de hoy.</span>
            </div>
          )}

          {/* Barra de acciones */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm font-semibold">
              {current ? (
                <>
                  <Unlock size={16} className="text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400">Caja abierta</span>
                  {current.shift_label && <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-content-2">Turno {current.shift_label}</span>}
                </>
              ) : (
                <>
                  <Lock size={16} className="text-content-3" />
                  <span className="text-content-2">No hay caja abierta</span>
                  {turno
                    ? <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-normal text-content-3">Turno {turno.label} · {horaBonita(turno.inicio.toTimeString().slice(0, 5))}–{horaBonita(turno.fin.toTimeString().slice(0, 5))}</span>
                    : <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs font-normal text-content-3">Hoy no hay turno configurado</span>}
                </>
              )}
            </span>
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                {/* Karla 30-sep: la persona llega, paga la clase y entra — nombre, teléfono y clase, y queda como prospecto. */}
                <button onClick={() => setClaseSuelta(true)} data-testid="btn-clase-suelta"
                  className="flex items-center gap-1.5 rounded-lg border border-brand-blue/40 bg-brand-blue/10 px-3 py-1.5 text-sm font-medium text-brand-blue transition hover:bg-brand-blue/20">
                  <Ticket size={15} /> Clase suelta
                </button>
                {hasModule('vitrina') && (
                  <button onClick={() => setVenta(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                    <ShoppingBag size={15} /> Venta de vitrina
                  </button>
                )}
                {current ? (
                  <>
                    {/* Depósito y retiro, cada uno con su color, como en Kydemy */}
                    <button onClick={() => { setMovKind('deposit'); setModal('movement'); }}
                      className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-500/20 dark:text-emerald-300">
                      <Plus size={15} /> Depósito
                    </button>
                    <button onClick={() => { setMovKind('withdrawal'); setModal('movement'); }}
                      className="flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-500/20 dark:text-red-300">
                      <Minus size={15} /> Retiro
                    </button>
                    {/* 10-oct-2026 (arqueo de Dayan): pagaron con dólares algo registrado en pesos. */}
                    <button onClick={() => { setMovKind('usd_in'); setModal('movement'); }} data-testid="btn-entraron-dolares"
                      className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-surface px-3 py-1.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-500/10 dark:text-emerald-300">
                      <DollarSign size={15} /> Entraron dólares
                    </button>
                    <button onClick={() => setModal('close')}
                      className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
                      <Lock size={15} /> Cerrar / Arquear
                    </button>
                  </>
                ) : (
                  <button onClick={() => setModal('open')}
                    className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
                    <Banknote size={16} /> Abrir caja
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Resumen en vivo de la caja abierta — esto lo tenemos y Kydemy no: el esperado se
              ve sin esperar al corte. Se conserva a propósito. */}
          {current && (
            <div className={`mb-4 grid grid-cols-2 gap-4 rounded-2xl border border-emerald-500/30 bg-surface p-4 shadow-sm ring-1 ring-emerald-500/20 ${fiadoTurno ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
              <div>
                <div className="text-xs text-content-3">Efectivo esperado</div>
                <div className="font-display text-2xl font-bold text-content"><CountUp value={current.expected_amount} format={money} /></div>
                {current.usd && (
                  <div data-testid="usd-esperado" className="mt-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    + {current.usd.expected} USD en dólares{current.fx_rate ? ` · TC ${current.fx_rate}` : ''}
                  </div>
                )}
                {(current.adoptado_efectivo || 0) > 0 && (
                  <div data-testid="nota-adoptado" className="mt-0.5 text-xs text-content-3">
                    Incluye {money(current.adoptado_efectivo || 0)} de cobros con la caja cerrada (contados al abrir)
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-content-3">Fondo inicial</div>
                <div className="text-lg font-semibold text-content-2">{money(current.open_amount)}</div>
                {/* Un clic: con qué billetes y monedas se abrió (¿hay que ir por cambio?) */}
                <button data-testid="ver-billetes-apertura"
                  onClick={() => setVerBilletes({ titulo: 'Billetes de la apertura', json: current.open_breakdown, importe: Number(current.open_amount) || 0,
                    nota: `abrió ${current.opened_by_name ? current.opened_by_name.split(' ')[0] + ' · ' : ''}${fmtDateTime(current.opened_at)}` })}
                  className="mt-1 flex items-center gap-1 rounded-lg border border-line bg-surface-2 px-2 py-1 text-xs font-medium text-content-2 transition hover:bg-surface">
                  <Banknote size={13} /> Ver billetes
                </button>
              </div>
              <div>
                <div className="text-xs text-content-3">Cobrado en efectivo</div>
                <div className="text-lg font-semibold text-content-2">{money(current.cash_collected)} <span className="text-xs text-content-3">({current.num_payments})</span></div>
              </div>
              {/* Lo fiado en este turno (Carlos 29-sep): no es dinero, por eso va aparte del esperado. */}
              {fiadoTurno && (
                <div data-testid="resumen-deudas" title="Lo que se fió en este turno y sigue sin pagarse. Lo de alumnas ya está sumado en el efectivo esperado: al cierre tiene que estar ese dinero. Lo de maestros se acumula y no suma.">
                  <div className="text-xs text-content-3">Deudas del turno</div>
                  <div className={`text-lg font-semibold ${fiadoTurno.pendiente > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-content-2'}`}>
                    {money(fiadoTurno.pendiente)} <span className="text-xs text-content-3">({fiadoTurno.n})</span>
                  </div>
                  {fiadoTurno.pendiente > 0 && (
                    <div className="text-[11px] text-content-3">
                      {fiadoTurno.alumnas > 0 && <>alumnas {money(fiadoTurno.alumnas)} → en el esperado</>}
                      {fiadoTurno.alumnas > 0 && fiadoTurno.maestros > 0 && ' · '}
                      {fiadoTurno.maestros > 0 && <>maestros {money(fiadoTurno.maestros)}</>}
                    </div>
                  )}
                </div>
              )}
              <div>
                <div className="text-xs text-content-3">Abierta</div>
                <div className="text-sm text-content-2">{fmtDateTime(current.opened_at)}</div>
                {current.opened_by_name && <div className="text-xs text-content-3">por {current.opened_by_name}</div>}
              </div>
            </div>
          )}

          {/* Comisión por entregar (Mabel 5-oct): retiro de caja + nómina en un paso, sin el Excel. */}
          <div className="mb-4 empty:hidden"><ComisionPorEntregar modo="caja" cajaAbierta={!!current} onDelivered={refrescaTodo} /></div>

          {/* ── DOS COLUMNAS: días del mes · movimientos del día ── */}
          <div className="grid gap-4 lg:grid-cols-[17rem_1fr]">
            {/* Columna de días. En escritorio es una columna fija; en la TABLET DEL MOSTRADOR
                (que es donde se usa) va plegada, porque si no la lista se come la pantalla y
                hay que bajar media página para ver los movimientos del día. */}
            <div className="no-print rounded-2xl border border-line bg-surface shadow-sm">
              <div className="flex items-center gap-1.5 border-b border-line p-2.5">
                {soloHoy ? (
                  <span className="flex-1 px-1 text-sm font-medium text-content-2" data-testid="caja-solo-hoy">Cortes de hoy</span>
                ) : <>
                <select value={mes} onChange={(e) => setMes(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue">
                  {(meses.some((m) => m.ym === mes) ? meses : [{ ym: mes, n: 0 }, ...meses]).map((m) => (
                    <option key={m.ym} value={m.ym}>{mesBonito(m.ym)}{m.n ? ` (${m.n})` : ''}</option>
                  ))}
                </select>
                <a href={cashboxExportUrl({ month: mes })} title="Descargar el mes en Excel"
                  className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                  <Download size={14} /> Excel
                </a>
                </>}
              </div>

              {/* Móvil/tablet: cabecera con el día elegido que despliega la lista */}
              <button onClick={() => setListaAbierta((v) => !v)}
                className="flex w-full items-center gap-2 border-b border-line px-3 py-2.5 text-left lg:hidden">
                {listaAbierta ? <ChevronDown size={16} className="shrink-0 text-content-3" /> : <ChevronRight size={16} className="shrink-0 text-content-3" />}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-content">
                  {(() => {
                    const d = dias.find((x) => x.cashbox_id === sel);
                    if (!d) return `Elegir día · ${dias.length} en el mes`;
                    const f = new Date(String(d.closed_at || d.opened_at).replace(' ', 'T'));
                    return isNaN(f.getTime()) ? 'Día seleccionado'
                      : f.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long' });
                  })()}
                </span>
                <span className="shrink-0 text-xs text-content-3">{dias.length} días</span>
              </button>

              <div className={`${listaAbierta ? 'block' : 'hidden'} max-h-[50vh] space-y-0.5 overflow-y-auto p-1.5 lg:block lg:max-h-[70vh]`}>
                {dias.length === 0 ? (
                  <p className="px-2 py-6 text-center text-sm text-content-3">Sin cajas en {mesBonito(mes)}.</p>
                ) : dias.map((c) => (
                  <DiaItem key={c.cashbox_id} c={c}
                    activo={sel === c.cashbox_id}
                    onClick={() => { setSel(c.cashbox_id); setListaAbierta(false); }} />
                ))}
              </div>
            </div>

            {/* Movimientos del día — `print-area`: es lo único que sale al imprimir/guardar PDF */}
            <div className="print-area min-w-0 rounded-2xl border border-line bg-surface shadow-sm">
              {cargandoLedger && !ledger ? (
                <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : !box ? (
                <EmptyState icon={Banknote} title="Elige un día" subtitle="Selecciona una caja de la izquierda para ver sus movimientos." />
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
                    <div className="min-w-0">
                      <h3 className="flex items-center gap-2 font-display text-base font-bold text-content">
                        <Receipt size={16} /> Movimientos
                        {verArqueoAbierto && <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">En curso</span>}
                        {box.source === 'kydemy' && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-normal text-content-3">Kydemy</span>}
                      </h3>
                      <p className="mt-0.5 text-xs text-content-3">
                        {fmtDateTime(box.opened_at)}
                        {box.closed_at && ` → ${String(box.closed_at).slice(11, 16)}`}
                        {box.shift_label && ` · Turno ${box.shift_label}`}
                        {box.opened_by_name && ` · abrió ${box.opened_by_name.split(' ')[0]}`}
                        {box.closed_by_name && ` · cerró ${box.closed_by_name.split(' ')[0]}`}
                      </p>
                    </div>
                    <div className="no-print flex items-center gap-1.5">
                      <a href={cashboxExportUrl({ cashbox_id: box.cashbox_id })} title="Descargar este corte en Excel"
                        className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                        <Download size={14} /> Excel
                      </a>
                      <button onClick={() => window.print()} title="Imprimir o guardar el corte como PDF"
                        className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                        <Printer size={14} /> PDF
                      </button>
                    </div>
                  </div>

                  {/* Aviso de corte importado incompleto (se dice, no se disimula).
                      🪤 Se cuentan SOLO los cobros en efectivo: el `num_payments` de Kydemy también
                      lo es (comprobado en sus 18 cajas de septiembre). Antes se comparaba contra
                      TODOS los cobros, así que las transferencias inflaban la cuenta y el aviso
                      podía callarse justo cuando sí faltaba detalle del cajón. */}
                  {(() => {
                    const cobros = filasEfectivo.filter((f) => f.kind === 'payment').length;
                    if (!(box.source === 'kydemy' && box.num_payments != null && cobros < box.num_payments)) return null;
                    return (
                      <div className="border-b border-line/50 px-4 py-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                        Corte importado de Kydemy: aquí se ven {cobros} de los {box.num_payments} cobros que cerró Kydemy
                        (su detalle no vino completo en la importación). Los totales del corte son los suyos.
                      </div>
                    );
                  })()}

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-line bg-surface-2/60 text-content-3">
                        <tr className="text-[11px] uppercase tracking-wide">
                          <th className="w-12 px-2 py-1.5 text-left font-medium sm:w-14 sm:px-3">Hora</th>
                          {/* 🚨 19-sep-2026 — en un teléfono de 390px esta tabla pedía 575 y lo que
                              se salía eran CANTIDAD y ACUMULADO: en una caja, las dos únicas
                              columnas que importan. El `overflow-x-auto` de arriba dejaba
                              arrastrar, pero nada dice que haya más a la derecha. Se van del
                              móvil el avatar del cajero (su nombre ya se ocultaba) y el
                              acumulado, que es dato de auditoría y vive también en el pie. */}
                          <th className="hidden px-2 py-1.5 text-left font-medium sm:table-cell"><span className="hidden lg:inline">Cajero</span></th>
                          <th className="px-2 py-1.5 text-left font-medium">Alumno</th>
                          <th className="px-2 py-1.5 text-left font-medium">Detalle</th>
                          <th className="w-[5.25rem] px-1 py-1.5 text-right font-medium sm:w-20 sm:px-1.5 lg:w-24 lg:px-2">Cantidad</th>
                          <th className="hidden w-24 px-2 py-1.5 text-right font-medium sm:table-cell lg:w-28 lg:px-3">Acumulado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filasEfectivo.map((f, i) => {
                          const chip = CHIP[f.kind] || CHIP.payment;
                          const esCierre = f.kind === 'close';
                          const esDeuda = f.kind === 'debt';
                          // Fiado ya pagado: sigue en el turno en que se fió, pero sin alarma.
                          const resta = esDeuda ? Number(f.remaining ?? f.amount) : 0;
                          const debeAun = esDeuda && resta > 0;
                          // El acumulado anterior es el del último renglón que SÍ movió el cajón.
                          const accPrevio = filasEfectivo.slice(0, i).reverse().find((x) => x.acumulado != null)?.acumulado ?? 0;
                          return (
                            <tr key={`${f.kind}-${f.payment_id ?? f.debt_id ?? i}`}
                              data-testid={esDeuda ? 'mov-fiado' : undefined}
                              title={esDeuda ? (debeAun
                                ? `${f.name || 'Alguien'} DEBE ${money(resta)}${resta < f.amount ? ` de ${money(f.amount)}` : ''} — fiado, no entró dinero al cajón. ${
                                  f.es_maestro ? 'Es del equipo: se le acumula y la caja puede cerrar sin cobrarlo.' : 'Cuenta como ingreso: al cierre tiene que estar ese dinero, o cobrado o puesto por quien está en caja.'}`
                                : `Fiado de ${money(f.amount)} ya pagado`) : undefined}
                              className={`border-t border-line/60 ${esCierre ? 'bg-amber-500/5' : ''} ${
                                debeAun ? 'bg-orange-500/10 transition hover:bg-orange-500/20' : esDeuda ? 'opacity-70' : ''}`}>
                              <td className="px-2 py-1.5 tabular-nums text-xs text-content-3 sm:px-3">{String(f.at).slice(11, 16)}</td>
                              <td className="hidden px-2 py-1.5 sm:table-cell">
                                {f.teller ? (
                                  <PerfilLink to={rutaPerfil({ userId: f.teller_id, esProfe: f.teller_es_profe })} title={f.teller}
                                    className="flex items-center gap-1.5">
                                    <Avatar kind="user" id={f.teller_id} name={f.teller} />
                                    <span className="hidden truncate text-xs text-content-2 lg:inline">{f.teller.split(' ').slice(0, 2).join(' ')}</span>
                                  </PerfilLink>
                                ) : <span className="text-xs text-content-3">—</span>}
                              </td>
                              <td className="px-2 py-1.5">
                                {f.name ? (
                                  // Clic → perfil de la alumna o del maestro que compró (Carlos 29-sep).
                                  <PerfilLink to={rutaPerfil({ contactId: f.contact_id, userId: f.payer_user_id, esProfe: 1 })} title={f.name}
                                    className="flex items-center gap-1.5 text-content">
                                    {/* La foto del alumno se va en teléfono: eran los últimos 34px que
                                        empujaban CANTIDAD fuera del recuadro, y el nombre gana sitio. */}
                                    <span className="hidden sm:inline-flex"><Avatar kind="contact" id={f.contact_id} name={f.name} /></span>
                                    <span className="max-w-[5.5rem] truncate sm:max-w-[9rem] lg:max-w-[15rem]">{f.name}</span>
                                  </PerfilLink>
                                ) : <span className="text-content-3">—</span>}
                              </td>
                              <td className="px-2 py-1.5">
                                <span className="flex flex-wrap items-center gap-1.5">
                                  <span className={`rounded px-1 py-0.5 text-[9px] font-medium uppercase sm:px-1.5 sm:text-[10px] ${
                                    esDeuda && !debeAun ? 'bg-surface-2 text-content-3' : chip.cls}`}>
                                    {esDeuda && !debeAun ? 'Fiado · pagado' : chip.txt}
                                  </span>
                                  {esDeuda && (
                                    <span className="hidden rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase text-content-3 lg:inline">
                                      {f.label === 'other' ? 'Otra deuda' : TYPE_LABEL[f.label] || f.label}
                                    </span>
                                  )}
                                  {esDeuda && f.es_maestro && debeAun && (
                                    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-content-3">maestro · no suma al cajón</span>
                                  )}
                                  {f.kind === 'payment' && (
                                    <>
                                      <span className="hidden rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase text-content-3 lg:inline">
                                        {TYPE_LABEL[f.label] || f.label}
                                      </span>
                                      <MetodoPago metodo={f.method} conTexto="sm" size={13} />
                                    </>
                                  )}
                                  {/* Acotado en teléfono: sin tope, un detalle largo fijaba el ancho de la
                                      columna y empujaba CANTIDAD fuera de la pantalla. */}
                                  {f.detail && <span className="max-w-[6rem] truncate text-xs text-content-3 sm:max-w-none">{esDeuda ? sinPrefijoVitrina(f.detail) : f.detail}</span>}
                                </span>
                              </td>
                              <td className={`px-1 py-1.5 text-right font-medium tabular-nums sm:px-1.5 lg:px-2 ${
                                f.amount < 0 ? 'text-red-600 dark:text-red-400'
                                  : debeAun ? 'text-orange-700 dark:text-orange-300' : esDeuda ? 'text-content-3 line-through' : 'text-content'}`}>
                                {/* Fiado con abonos: manda lo que FALTA, que es lo que se le va a cobrar. */}
                                {f.amount < 0 ? `−${money(Math.abs(f.amount))}` : debeAun ? money(resta) : money(f.amount)}
                                {debeAun && resta < f.amount && <span className="block text-[10px] font-normal text-content-3">de {money(f.amount)}</span>}
                                {/* Dólares: se cuadran aparte; al acumulado de pesos solo entra la parte en pesos (cobro mixto). */}
                                {f.currency === 'USD' && <span data-testid="mov-usd" className="block text-[10px] font-normal text-content-3">{textoDolares(f.usd_amount, f.mxn_amount)}</span>}
                              </td>
                              <td className={`hidden px-2 py-1.5 text-right font-semibold tabular-nums sm:table-cell lg:px-3 ${
                                f.acumulado == null ? 'text-content-3'
                                  : i > 0 && accPrevio > f.acumulado ? 'text-red-600 dark:text-red-400' : 'text-content-2'}`}>
                                {f.acumulado == null ? '—' : money(f.acumulado)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Los cobros que NO son efectivo: se dicen, no se esconden (ver arriba el
                      porqué del filtro). Un clic lleva a Ingresos, donde sí es su sitio. */}
                  {otrosMetodos.n > 0 && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line bg-surface-2/40 px-4 py-2 text-xs text-content-3">
                      <CreditCard size={13} className="shrink-0" />
                      <span>
                        {otrosMetodos.n} cobro{otrosMetodos.n > 1 ? 's' : ''} más por {otrosMetodos.detalle} ·{' '}
                        <b className="tabular-nums text-content-2">{money(otrosMetodos.total)}</b> — no entran al cajón.
                      </span>
                      <Link to="/caja?t=ingresos" className="font-medium text-accent-content underline-offset-2 hover:underline">
                        Verlos en Ingresos →
                      </Link>
                    </div>
                  )}

                  {/* Pie del corte: las 4 pastillas + diferencia */}
                  {ledger!.totales && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        {verArqueoAbierto ? (
                          <span className="rounded-full bg-surface-2 px-2.5 py-1 text-content-2">
                            En el cajón <b className="tabular-nums text-content">{money(ledger!.acumulado_final)}</b>
                          </span>
                        ) : (
                          <>
                            <span className="rounded-full bg-surface-2 px-2.5 py-1 text-content-2">Esperado <b className="tabular-nums text-content">{ledger!.totales.esperado != null ? money(ledger!.totales.esperado) : '—'}</b></span>
                            <span className="rounded-full bg-sky-500/15 px-2.5 py-1 text-sky-700 dark:text-sky-300">Contado <b className="tabular-nums">{ledger!.totales.contado != null ? money(ledger!.totales.contado) : '—'}</b></span>
                          </>
                        )}
                        <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-emerald-700 dark:text-emerald-300">Entradas <b className="tabular-nums">{money(ledger!.totales.entradas)}</b></span>
                        <span className="rounded-full bg-red-500/15 px-2.5 py-1 text-red-700 dark:text-red-300">Salidas <b className="tabular-nums">{money(ledger!.totales.salidas)}</b></span>
                        {/* Lo que se fió en el turno y sigue sin pagarse. No es dinero del cajón: no
                            entra al esperado ni a la diferencia. Sale siempre, como Entradas y Salidas. */}
                        <span data-testid="pie-deudas"
                          title={(ledger!.totales.fiados_n || 0) > 0
                            ? `${ledger!.totales.fiados_n} fiado${ledger!.totales.fiados_n === 1 ? '' : 's'} en este turno por ${money(ledger!.totales.fiado || 0)}; siguen debiendo ${money(ledger!.totales.fiado_pendiente || 0)}. Lo de alumnas (${money(ledger!.totales.fiado_alumnas || 0)}) cuenta como ingreso y ya está en el esperado; lo de maestros (${money(ledger!.totales.fiado_maestros || 0)}) se acumula y no suma.`
                            : 'Nadie quedó debiendo en este turno.'}
                          className={`rounded-full px-2.5 py-1 ${(ledger!.totales.fiado_pendiente || 0) > 0
                            ? 'bg-orange-500/20 text-orange-700 dark:text-orange-300' : 'bg-surface-2 text-content-3'}`}>
                          Deudas <b className="tabular-nums">{money(ledger!.totales.fiado_pendiente || 0)}</b>
                          {(ledger!.totales.fiado || 0) > (ledger!.totales.fiado_pendiente || 0) && (
                            <span className="font-normal opacity-80">{' '}de {money(ledger!.totales.fiado || 0)} fiados</span>
                          )}
                          {(ledger!.totales.fiado_maestros || 0) > 0 && (ledger!.totales.fiado_alumnas || 0) > 0 && (
                            <span className="font-normal opacity-80">{' '}· alumnas {money(ledger!.totales.fiado_alumnas || 0)} · maestros {money(ledger!.totales.fiado_maestros || 0)}</span>
                          )}
                          {(ledger!.totales.fiado_maestros || 0) > 0 && !((ledger!.totales.fiado_alumnas || 0) > 0) && (
                            <span className="font-normal opacity-80">{' '}· maestros</span>
                          )}
                        </span>
                      </div>
                      {!verArqueoAbierto && ledger!.totales.diferencia != null && (
                        <span className={`text-sm font-semibold ${diffColor(ledger!.totales.diferencia)}`}>
                          Diferencia: {ledger!.totales.diferencia === 0 ? '$0.00 (cuadró)' : diffLabel(ledger!.totales.diferencia)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* El conteo de billetes — lo único que a Karla le gustó, ahora visible */}
                  {/* 29-sep (Carlos: «veo el del 28 y no tengo idea con cuántos billetes cerraron»):
                      una caja CERRADA sin conteo ya no se calla — dice que solo hay importe y por
                      qué. Las de Kydemy nunca lo tendrán: su API no guarda billetes (verificado). */}
                  {/* 29-sep (Carlos): dos tarjetas-botón, apertura y cierre, en vez de la línea de texto
                      «Billetes a la apertura: 2×$500 · …» + enlaces sueltos — era redundante. Sin conteo
                      la tarjeta sale apagada y lo dice (las de Kydemy nunca lo tendrán). */}
                  <div className="no-print grid grid-cols-1 gap-2 border-t border-line bg-surface-2/40 px-4 py-3 sm:grid-cols-2" data-testid="billetes-turno">
                    <TarjetaBilletes etapa="Apertura" json={box.open_breakdown} importe={box.open_amount} kydemy={box.source === 'kydemy'}
                      onVer={() => setVerBilletes({ titulo: 'Billetes de la apertura', json: box.open_breakdown, importe: Number(box.open_amount) || 0 })} />
                    {box.closed_at && (
                      <TarjetaBilletes etapa="Cierre" json={box.close_breakdown} importe={box.close_amount} kydemy={box.source === 'kydemy'}
                        onVer={() => setVerBilletes({ titulo: 'Billetes del cierre', json: box.close_breakdown, importe: Number(box.close_amount) || 0 })} />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {isAdmin && !soloHoy && config && (
            <div className="mt-3 flex justify-end">
              <button onClick={() => setModal('config')}
                className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-content-3 transition hover:bg-surface-2 hover:text-content">
                <Settings size={14} /> Configurar caja
              </button>
            </div>
          )}
        </>
      )}

      {modal === 'open' && (
        // 30-sep-2026 (Karla, video «Apertura de caja»): contar billetes también es OBLIGATORIO al abrir.
        <DenominationModal title={`Abrir caja${turno ? ` · Turno ${turno.label}` : ''}`} cta="Abrir caja" conteoObligatorio accion="abrir"
          expected={lastClose ? Number(lastClose.close_amount) + pendienteEfectivo : undefined}
          expectedLabel={pendienteEfectivo > 0 ? 'Debe haber (turno anterior + cobros con la caja cerrada)' : 'El turno anterior dejó'}
          sugerido={lastClose ? Number(lastClose.close_amount) + pendienteEfectivo : null}
          sugeridoBreakdown={pendienteEfectivo > 0 ? null : (lastClose?.close_breakdown || null)}
          aviso={pendientes.length > 0 ? (
            <div data-testid="aviso-cobros-caja-cerrada" className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-content-2">
              <b className="text-content">Cobros registrados con la caja cerrada</b> — se suman a esta caja:
              <ul className="mt-1 space-y-0.5 text-xs">
                {pendientes.map((p) => (
                  <li key={p.payment_id}>
                    <b className="text-content">{p.currency === 'USD' ? textoDolares(p.usd_amount, p.mxn_amount) : money(p.amount)}</b> {p.currency === 'USD' ? 'en dólares' : (METHOD_LABEL[p.method] || p.method).toLowerCase()}
                    {' · '}{p.teller ? p.teller.split(' ')[0] : '—'} {p.hora}{p.name ? ` · ${p.name}` : ''}
                  </li>
                ))}
              </ul>
              {lastClose && pendienteEfectivo > 0 && (
                <div className="mt-1 text-xs text-content-3">
                  {money(Number(lastClose.close_amount))} del turno anterior + {money(pendienteEfectivo)} en efectivo = {money(Number(lastClose.close_amount) + pendienteEfectivo)} en el cajón. Transferencias y tarjeta no mueven el efectivo.
                </div>
              )}
            </div>
          ) : undefined}
          expectedHint={lastClose
            ? `${lastClose.shift_label ? `Turno ${lastClose.shift_label} · ` : ''}${fmtDateTime(lastClose.closed_at)}${lastClose.closed_by_name ? ` · ${lastClose.closed_by_name.split(' ')[0]}` : ''} — si no cuadra, se abre igual y queda anotado.`
            : undefined}
          usdEsperado={lastClose?.close_usd != null || pendienteUsd > 0 ? Math.round((Number(lastClose?.close_usd || 0) + pendienteUsd) * 100) / 100 : null}
          borradorKey={borradorAbrir} onClose={() => setModal(null)} onSubmit={doOpen} />
      )}
      {modal === 'close' && current && (
        <DenominationModal title="Cerrar / Arquear" cta="Cerrar caja" conteoObligatorio
          expected={current.expected_amount} sugerido={current.expected_amount}
          usdEsperado={current.usd ? current.usd.expected : null}
          borradorKey={borradorCerrar} onClose={() => setModal(null)} onSubmit={doClose} />
      )}
      {modal === 'movement' && current && (
        <MovementModal initialKind={movKind} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); toast.success('Movimiento registrado'); refrescaTodo(); }} />
      )}
      {modal === 'config' && config && (
        <ConfigModal cfg={config} onClose={() => setModal(null)} onSaved={applyConfig} />
      )}
      {waModal && config && (
        <WhatsAppReportModal title={waModal.title} text={waModal.text} phone={config.corte.phone} onClose={() => setWaModal(null)} />
      )}
      {verBilletes && <BilletesModal {...verBilletes} onClose={() => setVerBilletes(null)} />}
      {claseSuelta && <ClaseSueltaModal cajaAbierta={!!current} onClose={() => setClaseSuelta(false)} onDone={() => { setClaseSuelta(false); refrescaTodo(); }} />}
      {venta && <VentaVitrinaModal onClose={() => setVenta(false)} onSold={() => { setVenta(false); refrescaTodo(); }} />}
    </div>
  );
}
